import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Ticket, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  return h < 24 ? `hace ${h} h` : new Date(iso).toLocaleDateString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "2-digit", year: "numeric" });
};

export function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/business/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/business/notifications/${n.id}/read`).catch(() => {});
    setOpen(false); load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await api.post("/business/notifications/read-all").catch(() => {}); load(); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={`Avisos${data.unread ? `: ${data.unread} sin leer` : ""}`} data-testid="notif-bell">
          <Bell className={`w-5 h-5 ${data.unread ? "text-primary" : ""}`} />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center ring-2 ring-background animate-in zoom-in-50" data-testid="notif-count">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={12} sideOffset={8} className="w-[min(22rem,calc(100vw-1.5rem))] p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-semibold text-sm">Avisos</p>
          {data.unread > 0 && <button onClick={readAll} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="notif-read-all"><CheckCheck className="w-3.5 h-3.5" />Marcar todo como leído</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="p-6 text-sm text-center text-muted-foreground" data-testid="notif-empty">No tienes avisos por ahora.</p>}
          {data.items.map((n) => (
            <button key={n.id} type="button" onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-4 py-3 flex gap-3 border-b last:border-0 hover:bg-muted/60 transition-colors ${n.read ? "" : "bg-primary/5"}`}>
              <span className="w-8 h-8 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0"><Ticket className="w-4 h-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2"><span className="text-sm font-semibold">{n.title}</span>{!n.read && <span className="w-2 h-2 rounded-full bg-coral shrink-0" />}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{n.message}</span>
                <span className="block text-[10px] text-muted-foreground mt-1">{ago(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
